import { useEffect, useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { Field } from '../../components/Field.js';
import { FormBanner } from '../../components/FormBanner.js';
import { Icon } from '../../components/Icon.js';
import { Logo } from '../../components/Logo.js';
import { errorMessage, isApiRequestError } from '../../lib/api/index.js';
import { useAdminAuthStore } from '../adminAuthStore.js';

interface FromState {
  from?: { pathname?: string };
}

/**
 * Administrator sign-in at `/admin/login`.
 *
 * The credentials are the ones in the server's environment (`ADMIN_USERNAME`,
 * `ADMIN_PASSWORD`). There is deliberately no link to the user sign-in, and no
 * "forgot password": the administrator is not an account.
 */
export function AdminLoginPage() {
  const status = useAdminAuthStore((state) => state.status);
  const bootstrap = useAdminAuthStore((state) => state.bootstrap);
  const login = useAdminAuthStore((state) => state.login);
  const pending = useAdminAuthStore((state) => state.pending);
  const navigate = useNavigate();
  const location = useLocation();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (status === 'loading') {
      void bootstrap();
    }
  }, [status, bootstrap]);

  if (status === 'authenticated') {
    return <Navigate to="/admin" replace />;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);

    if (!username.trim() || !password) {
      setError('Enter the administrator username and password.');
      return;
    }

    try {
      await login({ username: username.trim(), password });
      const state = location.state as FromState | null;
      await navigate(state?.from?.pathname ?? '/admin', { replace: true });
    } catch (cause) {
      // 401 stays vague; 403 (not configured) and 429 (locked out) carry the
      // server's own explanation, which is the useful part.
      setError(
        isApiRequestError(cause) && cause.status === 401
          ? 'Username or password is incorrect.'
          : errorMessage(cause),
      );
    }
  }

  return (
    <main className="admin-login">
      <form className="admin-login-card" onSubmit={(event) => void handleSubmit(event)} noValidate>
        <span className="brand">
          <Logo size={28} />
        </span>
        <h1>
          <Icon name="shieldCheck" size={22} />
          Administration
        </h1>
        <p className="subtitle">
          Sign in with the administrator credentials configured on the server.
        </p>

        <FormBanner message={error} />

        <Field
          label="Username"
          name="admin-username"
          autoComplete="username"
          value={username}
          onChange={(event) => setUsername(event.target.value)}
          required
        />
        <Field
          label="Password"
          name="admin-password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          required
        />

        <button type="submit" className="btn btn--primary btn--lg btn--block" disabled={pending}>
          {pending ? <span className="btn-spinner" /> : <Icon name="lock" size={15} />}
          {pending ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </main>
  );
}
